import api from './api';

export type MctCategory = 'cadre' | 'agent_maitrise' | 'employe' | 'ouvrier';

export interface MctCollaboratorUser {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
}

export interface MctCollaboratorItem {
  id: number;
  matricule: string;
  user_id: number | null;
  user: MctCollaboratorUser | null;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string | null;
  category: MctCategory;
  discount_rate: number;
  max_splits: number;
  used_splits: number;
  remaining_splits: number;
  is_active: boolean;
  linked_at: string | null;
  is_linked: boolean;
  notes: string | null;
  created_at: string;
  referrals_count: number;
  total_entourage_savings: number;
}

export interface MctCollaboratorStats {
  total_collaborators: number;
  active_collaborators: number;
  linked_collaborators: number;
  by_category: {
    cadre?: number;
    agent_maitrise?: number;
    employe?: number;
    ouvrier?: number;
  };
  splits: {
    total_max: number;
    total_used: number;
    total_remaining: number;
  };
  entourage: {
    total_orders: number;
    total_savings: number;
  };
}

export interface MctEntourageOrder {
  id: number;
  sector: 'maintenance' | 'installation' | 'repair';
  discount_amount: number;
  discount_rate: number;
  used_at: string;
  filleul: {
    id: number;
    first_name: string;
    last_name: string;
    phone: string | null;
    email: string | null;
  };
  collaborator: {
    id: number;
    matricule: string;
    first_name: string;
    last_name: string;
    category: MctCategory;
  };
  intervention?: {
    id: number;
    reference: string;
    title: string;
    intervention_type: string;
    total_price: number;
    status: string;
  };
}

export interface CollaboratorFilters {
  search?: string;
  category?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface CollaboratorsResponse {
  success: boolean;
  data: MctCollaboratorItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const mctCollaboratorService = {
  async getCollaborators(filters: CollaboratorFilters = {}): Promise<CollaboratorsResponse> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const response = await api.get(`/admin/collaborators?${params.toString()}`);
    return response.data;
  },

  async createCollaborator(data: {
    matricule: string;
    first_name: string;
    last_name: string;
    phone?: string;
    category: MctCategory;
    discount_rate?: number;
    max_splits?: number;
    notes?: string;
  }): Promise<{ success: boolean; message: string; data: MctCollaboratorItem }> {
    const response = await api.post('/admin/collaborators', data);
    return response.data;
  },

  async updateCollaborator(
    id: number,
    data: Partial<{
      first_name: string;
      last_name: string;
      phone: string;
      category: MctCategory;
      discount_rate: number;
      max_splits: number;
      used_splits: number;
      is_active: boolean;
      notes: string;
    }>
  ): Promise<{ success: boolean; message: string; data: MctCollaboratorItem }> {
    const response = await api.put(`/admin/collaborators/${id}`, data);
    return response.data;
  },

  async unlinkCollaborator(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/admin/collaborators/${id}/unlink`);
    return response.data;
  },

  async deleteCollaborator(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/admin/collaborators/${id}`);
    return response.data;
  },

  async bulkImportCollaborators(collaborators: Array<{
    matricule: string;
    first_name: string;
    last_name: string;
    category?: string;
    phone?: string;
  }>): Promise<{
    success: boolean;
    message: string;
    data: {
      total_processed: number;
      created_count: number;
      updated_count: number;
      errors_count: number;
      errors: Array<{ row?: number; matricule?: string; error: string }>;
    };
  }> {
    const response = await api.post('/admin/collaborators/bulk-import', { collaborators });
    return response.data;
  },

  async getStats(): Promise<{ success: boolean; data: MctCollaboratorStats }> {
    const response = await api.get('/admin/collaborators/stats');
    return response.data;
  },

  async getEntourageOrders(page = 1, limit = 20): Promise<{
    success: boolean;
    data: MctEntourageOrder[];
    pagination: { total: number; page: number; limit: number; pages: number };
  }> {
    const response = await api.get(`/admin/collaborators/entourage-orders?page=${page}&limit=${limit}`);
    return response.data;
  }
};

export default mctCollaboratorService;
